import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";

function slotKey(row) {
  return String(row?.id || row?.orderId || row?.codification || "");
}

export default function SubscriptionBuySlotPanel({
  subscriptionPath,
  promoCode,
  stepNumber = 2,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = subscriptionPath
        ? `?path=${encodeURIComponent(subscriptionPath)}`
        : "";
      const res = await fetch(apiUrl(`/api/client/slots/pending${q}`), {
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(
          clientSafeMessage(data.message, "Could not load your slots."),
        );
        return;
      }
      const next = Array.isArray(data.rows) ? data.rows : [];
      setRows((prev) => {
        // Keep optimistic rows that the server might not return yet (path lag).
        const byKey = new Map();
        for (const r of next) byKey.set(slotKey(r), r);
        for (const r of prev) {
          const k = slotKey(r);
          if (k && !byKey.has(k)) byKey.set(k, r);
        }
        return Array.from(byKey.values());
      });
    } catch (e) {
      setError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setLoading(false);
    }
  }, [subscriptionPath]);

  useEffect(() => {
    void loadPending();
  }, [loadPending]);

  async function handleCreateSlot() {
    setCreating(true);
    setError(null);
    try {
      const body = { planId: "bronze" };
      if (subscriptionPath) body.subscriptionPath = subscriptionPath;
      const res = await fetch(apiUrl("/api/client/slots/create"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok || !data.row) {
        setError(
          clientSafeMessage(data.message, "Could not create slot."),
        );
        return;
      }
      setRows((prev) => {
        const k = slotKey(data.row);
        if (k && prev.some((r) => slotKey(r) === k)) return prev;
        return [...prev, data.row];
      });
      setLoading(false);
      void loadPending();
    } catch (e) {
      setError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setCreating(false);
    }
  }

  function payHref(row) {
    const params = new URLSearchParams();
    if (promoCode) params.set("promo", promoCode);
    if (subscriptionPath) params.set("path", subscriptionPath);
    const reserve = row?.orderId || row?.codification;
    if (reserve) params.set("reserve", reserve);
    if (row?.planId) params.set("plan", row.planId);
    return `/PayUSDT?${params.toString()}`;
  }

  return (
    <li className="space-y-3">
      <span className="font-semibold text-navy-700 dark:text-white">
        {stepNumber}. Buy your slot.
      </span>
      <div className="ml-0 rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-900/50">
        <p className="text-sm font-medium text-navy-700 dark:text-white">
          a. Create a slot
        </p>
        <button
          type="button"
          disabled={creating}
          onClick={() => void handleCreateSlot()}
          className="mt-3 inline-flex items-center justify-center rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-600 disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
        >
          {creating ? "Creating…" : "Create a slot"}
        </button>

        {error ? (
          <p className="mt-3 text-xs font-medium text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-4 space-y-3">
          {loading && rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading slots…
            </p>
          ) : null}

          {!loading && rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No slot yet. Click <strong>Create a slot</strong> to get your slot
              name, then pay.
            </p>
          ) : null}

          {rows.map((row) => {
            const name = row.codification || row.orderId || "—";
            const waitingApproval = row.status === "awaiting_approval";
            return (
              <div
                key={slotKey(row) || name}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-white/10 dark:bg-navy-900/60"
              >
                <button
                  type="button"
                  className="inline-flex items-center rounded-lg border border-brand-200 bg-white px-3 py-2 font-mono text-sm font-bold text-brand-700 shadow-sm dark:border-brand-500/30 dark:bg-navy-800 dark:text-brand-200"
                  title="Your slot name"
                >
                  {name}
                </button>

                {waitingApproval ? (
                  <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
                    Waiting for Admin Approval
                  </span>
                ) : (
                  <>
                    <Link
                      to={payHref(row)}
                      className="inline-flex items-center justify-center rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
                    >
                      Pay
                    </Link>
                    <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
                      {row.statusLabel || "Waiting for payment"}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </li>
  );
}
